import { FormItem, Heading, TextArea } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Form, Modal } from 'antd'
import { showToast } from 'common/utils'
import ExternalLink from 'components/icons/ExternalLink'
import { NAMESPACE } from 'locale'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import useMedicationSchedulePatternPause from '../../hooks/useMedicationSchedulePatternPause'
import { getMedicationIcon } from '../../utils/medicationUtils'

interface Medication {
  id: string
  name: string
}

type PauseMedicationModalProps = {
  open: boolean
  medication: Medication
  residentName: string
  onCancel: () => void
  onSuccess?: () => void
}

const PauseMedicationModal = ({
  open,
  medication,
  residentName,
  onCancel,
  onSuccess
}: PauseMedicationModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const [form] = Form.useForm()
  const reasonPaused = Form.useWatch('reasonPaused', form)

  const { pauseMedicationSchedulePattern, isPausingMedicationSchedulePattern } =
    useMedicationSchedulePatternPause({
      medicationId: medication.id,
      onSuccess: () => {
        showToast({
          type: 'success',
          title: t('PAUSE_MEDICATION_SUCCESS', { medication: medication.name })
        })
        form.resetFields()
        onSuccess?.()
      },
      onError: () => {
        showToast({
          type: 'error',
          title: t('PAUSE_MEDICATION_ERROR')
        })
        handleCancel()
      }
    })

  const handlePause = async () => {
    try {
      const values = await form.validateFields()
      await pauseMedicationSchedulePattern(values.reasonPaused)
    } catch (error) {
      // Validation failed
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={
        <FooterContainer>
          <ReadMoreLink
            href="https://support.logmycare.co.uk/en/collections/2749236-emar"
            target="_blank"
            rel="noreferrer"
          >
            {t('READ_MORE')} <ExternalLink />
          </ReadMoreLink>
          <ButtonsContainer>
            <Button onClick={handleCancel}>{t('CANCEL')}</Button>
            <Button
              variant="primary"
              onClick={handlePause}
              isLoading={isPausingMedicationSchedulePattern}
              disabled={!reasonPaused?.trim()}
            >
              {t('PAUSE_MEDICATION')}
            </Button>
          </ButtonsContainer>
        </FooterContainer>
      }
      width={630}
      title={
        <TitleContainer>
          {getMedicationIcon('paused')}
          <ModalTitle>
            <Heading level={3}>{t('PAUSE_MEDICATION')}</Heading>
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <Form form={form} preserve={false} layout="vertical">
        <ModalContentContainer>
          <Text>
            <Trans
              i18nKey="ARE_YOU_SURE_PAUSE_MEDICATION"
              ns={NAMESPACE.EMAR_V2}
              values={{
                medication: medication.name,
                name: residentName
              }}
              components={{ strong: <strong /> }}
            />
          </Text>

          <FormItem
            name="reasonPaused"
            label={<Text>{t('REASON_FOR_CHANGE')}</Text>}
            required
            full
            rules={[
              {
                required: true,
                message: t('REASON_FOR_CHANGE_REQUIRED')
              }
            ]}
          >
            <TextArea rows={2} placeholder={t('TYPE_HERE')} />
          </FormItem>

          <NoteContainer>
            <NoteText level={2}>{t('PAUSE_MEDICATION_NOTE')}</NoteText>
          </NoteContainer>
        </ModalContentContainer>
      </Form>

      <Divider />
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
`

const NoteContainer = styled.div`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  background: ${({ theme }) => theme.color.neutral.color10};
  padding: 12px;
`

const NoteText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-top: 12px;
  margin-bottom: 12px;
`

const ReadMoreLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: none;
  white-space: nowrap;
  font-size: 16px;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.color.status.primary};
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`

export default PauseMedicationModal
